import { useEffect, useRef, useState } from "react";
import { Video, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { CustomerInput } from "@/shared/components/ui/customer-input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Switch } from "@/shared/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useToast } from "@/shared/hooks/use-toast";
import { useSettings } from "@/shared/contexts/SettingsContext";
import { useSubscriptionGuard } from "@/shared/hooks/useSubscriptionGuard";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { apiClient } from "@/shared/services/apiClient";
import { supabase } from "@/integrations/supabase/client";
import { logAuthEvent, logAuthState } from "@/lib/authDebug";
import type { Session } from "@supabase/supabase-js";
import { useTeamMembers } from "@/shared/hooks/useTeamMembers";
import { useCustomers } from "@/shared/hooks/useCustomers";
import { useAuth } from "@/shared/contexts/AuthContext";
import { apiClient as apiClientV2 } from "@/shared/services/api";
import { getShareableBaseUrl } from "@/utils/getShareableBaseUrl";

interface StartLiveCallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefillData?: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
  };
}

export function StartLiveCallModal({ open, onOpenChange, prefillData }: StartLiveCallModalProps) {
  const { callSettingsMessage, organizationName, replaceTokens } = useSettings();
  const { t } = useTranslation();
  const { checkAccess } = useSubscriptionGuard();
  const { usage } = useSubscription();
  const { createCustomer } = useCustomers();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    countryCode: "+1",
    email: "",
    message: "",
    includeEmail: false,
    assignTo: ""
  });
  const { teamMembers, loading: loadingTeam } = useTeamMembers();
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(null);
  const sessionRef = useRef<Session | null>(null);

  const { toast } = useToast();
  const debugPrefix = "StartLiveCall";

  // Wait for Supabase session after hard refresh
  const getAccessTokenOrWait = async (timeoutMs = 1500): Promise<string> => {
    const start = Date.now();
    let { data: { session } } = await supabase.auth.getSession();
    while (!session && Date.now() - start < timeoutMs) {
      await new Promise((r) => setTimeout(r, 120));
      ({ data: { session } } = await supabase.auth.getSession());
    }
    return session?.access_token ?? "";
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      logAuthEvent(`${debugPrefix}:hydrate-check:start`);
      await logAuthState(`${debugPrefix}:hydrate-check:before-getSession`);
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      sessionRef.current = session;
      setIsAuthed(!!session);
      logAuthEvent(`${debugPrefix}:hydrate-check:result`, { hasSession: !!session });
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      logAuthEvent(`${debugPrefix}:onAuthStateChange`, { event, hasSession: !!session });
      if (!mounted) return;
      sessionRef.current = session;
      setIsAuthed(!!session);
    });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  // Fetch user role from team members
  useEffect(() => {
    if (user?.id && teamMembers.length > 0) {
      const currentMember = teamMembers.find(m => m.id === user.id);
      if (currentMember) {
        setUserRole(currentMember.role as 'admin' | 'user');
      }
    }
  }, [user, teamMembers]);

  // Set default assignTo to current user when teamMembers loads
  useEffect(() => {
    if (teamMembers.length > 0 && !formData.assignTo) {
      supabase.auth.getUser().then(({ data }) => {
        if (data?.user) {
          setFormData(prev => ({ ...prev, assignTo: data.user.id }));
        }
      });
    }
  }, [teamMembers, formData.assignTo]);

  // Prefill form data when prefillData prop changes
  useEffect(() => {
    if (prefillData && open) {
      setFormData(prev => {
        // Parse phone number to extract country code and number
        // Supports formats: +1XXXXXXXXXX, +1 XXXXXXXXXX, XXXXXXXXXX (digits-only from extension/HCP)
        let parsedCountryCode = prev.countryCode;
        let parsedPhoneNumber = prefillData.phoneNumber || prev.phoneNumber;

        if (prefillData.phoneNumber) {
          const phoneMatch = prefillData.phoneNumber.match(/^(\+\d+)\s?(.+)$/);
          if (phoneMatch) {
            parsedCountryCode = phoneMatch[1]; // e.g., "+1"
            parsedPhoneNumber = phoneMatch[2].replace(/\D/g, "").slice(0, 10); // e.g., "1234567890"
          } else {
            const digits = prefillData.phoneNumber.replace(/\D/g, "");
            if (digits.length >= 10) {
              parsedCountryCode = "+1";
              parsedPhoneNumber = digits.slice(-10);
            }
          }
        }

        const emailTrim = (prefillData.email || "").trim();
        return {
          ...prev,
          firstName: prefillData.firstName || prev.firstName,
          lastName: prefillData.lastName || prev.lastName,
          countryCode: parsedCountryCode,
          phoneNumber: parsedPhoneNumber,
          email: emailTrim || prev.email,
          includeEmail: emailTrim ? true : prev.includeEmail,
        };
      });
    }
  }, [prefillData, open]);

  // Generate message preview with tokens replaced
  const getMessagePreview = () => {
    const message = formData.message || callSettingsMessage;
    const customerFullName = `${formData.firstName} ${formData.lastName}`.trim();

    // Get assigned user's name from team members
    const assignedUser = teamMembers.find(tm => tm.id === formData.assignTo);
    const currentUserName = assignedUser?.name || '';

    return replaceTokens(message, {
      name: customerFullName || undefined, // Keep placeholder visible if no name
      firstName: formData.firstName.trim() || undefined,
      lastName: formData.lastName.trim() || undefined,
      organization: organizationName,
      date: new Date(),
      time: new Date(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userName: currentUserName || undefined
    });
  };

  // Generate final message for sending (replaces tokens with values or empty string)
  const getFinalMessage = () => {
    const message = formData.message || callSettingsMessage;
    const customerFullName = `${formData.firstName} ${formData.lastName}`.trim();

    // Get assigned user's name from team members
    const assignedUser = teamMembers.find(tm => tm.id === formData.assignTo);
    const currentUserName = assignedUser?.name || '';

    return replaceTokens(message, {
      name: customerFullName || '', // Replace with actual value or empty string
      firstName: formData.firstName.trim() || '',
      lastName: formData.lastName.trim() || '',
      organization: organizationName,
      date: new Date(),
      time: new Date(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userName: currentUserName || ''
    });
  };

  // 🔄 NEW handleSubmit using signaling /create-call endpoint
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logAuthEvent(`${debugPrefix}:handleSubmit:start`, { isAuthed });
    console.log('Form submitted with data:', formData);

    // SUBSCRIPTION GUARD: Check subscription access before proceeding
    if (!checkAccess('Start Live Call')) {
      return; // checkAccess handles toast and navigation
    }

    if (!formData.firstName.trim() || !formData.phoneNumber.trim()) {
      console.error('Missing required fields');
      toast({
        title: t("notifications.error"),
        description: "Please fill in customer first name and phone number",
        variant: "destructive"
      });
      return;
    }

    // Validate phone number - only allow digits and optional + at start
    const phoneRegex = /^\+?\d+$/;
    if (!phoneRegex.test(formData.phoneNumber.trim())) {
      toast({
        title: "Invalid Phone Number",
        description: "Phone number can only contain digits and an optional + at the beginning",
        variant: "destructive"
      });
      return;
    }

    const customerName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
    const fullPhoneNumber = `${formData.countryCode}${formData.phoneNumber.trim()}`;

    setIsLoading(true);

    try {

      const activeSession = sessionRef.current;
      logAuthEvent(`${debugPrefix}:handleSubmit:sessionRef`, { hasSession: !!activeSession, user: activeSession?.user?.id });

      if (!activeSession?.user) {
        throw new Error('No active session found. Please sign in again.');
      }

      logAuthEvent(`${debugPrefix}:handleSubmit:before-refreshSession`);
      let refreshedSession = sessionRef.current;
      if (!refreshedSession) {
        const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          logAuthEvent(`${debugPrefix}:handleSubmit:refresh-error`, { message: refreshError.message });
          throw new Error(`Session refresh failed: ${refreshError.message}`);
        }
        refreshedSession = refreshed.session ?? null;
        sessionRef.current = refreshedSession;
      }
      logAuthEvent(`${debugPrefix}:handleSubmit:after-refreshSession`, { hasSession: !!refreshedSession });

      // Save customer data to end_customers table using useCustomers hook
      const customer = await createCustomer({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        phone_number: fullPhoneNumber,
        email: formData.includeEmail ? formData.email.trim() || null : null,
        project: null,
        assigned_to: formData.assignTo || null,
      });

      if (!customer) {
        console.error("Error saving end customer");
        // Don't block the call if customer save fails
      }

      // Generate unique room ID
      const newRoomId = `room_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.log(`🆔 New room id: ${newRoomId}`);
      
      // Ensure we have an access token
      logAuthEvent(`${debugPrefix}:handleSubmit:before-accessToken`);
      const accessToken = await getAccessTokenOrWait();
      logAuthEvent(`${debugPrefix}:handleSubmit:accessToken`, { hasToken: !!accessToken });
      if (!accessToken) {
        throw new Error('You appear to be logged out. Please sign in again.');
      }
      
      // Create call session via signaling server (handles all DB logic)
      // SECURITY: Use backend API with authentication instead of direct fetch
      logAuthEvent(`${debugPrefix}:handleSubmit:before-createCallSession`);

      console.log('🔵 Creating call session with data:', {
        roomId: newRoomId,
        hostUserId: refreshedSession?.user?.id,
        customerName,
        phone: fullPhoneNumber,
        callType: 'live'
      });

      const sessionData = await apiClientV2.post<{
        ok: boolean;
        session?: any;
        callCode?: string;
        error?: string;
      }>('/create-call', {
        roomId: newRoomId,
        hostUserId: refreshedSession?.user?.id,
        customerData: {
          name: customerName,
          phone: fullPhoneNumber,
          email: formData.includeEmail ? formData.email.trim() || null : null
        },
        callType: 'live',
        assignedTo: formData.assignTo || null
      });

      console.log('📡 Create-call API response:', {
        ok: sessionData.ok,
        hasSession: !!sessionData.session,
        hasCallCode: !!sessionData.callCode,
        error: sessionData.error
      });

      if (!sessionData.ok) {
        console.error('❌ Error creating call session:', sessionData.error);
        throw new Error(sessionData.error || 'Failed to create call session');
      }

      console.log('✅ Call session created:', sessionData.session?.id);
      console.log('🔢 Call code:', sessionData.callCode);
      logAuthEvent(`${debugPrefix}:handleSubmit:createCallSession-success`, { sessionId: sessionData.session?.id, callCode: sessionData.callCode });

      // Create call link - CRITICAL: Guests must use /join/ to see phone verification
      const callLink = `${getShareableBaseUrl()}/join/${newRoomId}`;
      const messageText = `${getFinalMessage()}

${callLink}`;
      
      // Send SMS using the send-sms edge function
      console.log('Sending SMS to:', fullPhoneNumber);
      console.log('Message:', messageText);
      logAuthEvent(`${debugPrefix}:handleSubmit:before-sendSms`);

      // SECURITY: Use backend proxy instead of calling edge function directly
      const data = await apiClientV2.post<{ success: boolean; error?: string }>('/edge/send-sms', {
        to: fullPhoneNumber,
        message: messageText,
        type: 'live-call'
      });

      logAuthEvent(`${debugPrefix}:handleSubmit:smsResponse`, { success: data.success });

      if (!data.success) {
        console.error('SMS sending failed:', data);
        throw new Error(data.error || 'Failed to send SMS');
      }

      console.log('SMS sent successfully:', data);

      toast({
        title: t("notifications.success"),
        description: `SMS invitation sent to ${customerName} at ${fullPhoneNumber}`,
      });
      
      // Navigate to prejoin first (which then goes to video call)
      window.location.href = `/join/${newRoomId}?host=1`;
      
      // Close modal
      onOpenChange(false);
      
    } catch (error) {
      console.error('Error starting live call:', error);
      logAuthEvent(`${debugPrefix}:handleSubmit:error`, {
        message: error instanceof Error ? error.message : String(error)
      });
      toast({
        title: t("notifications.error"),
        description: error instanceof Error ? error.message : "Failed to start call",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    onOpenChange(false);
  };

  // Clear form when modal is closed
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFormData({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        countryCode: "+1",
        email: "",
        message: "",
        includeEmail: false,
        assignTo: ""
      });
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px] max-h-[80vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-primary/10 to-primary/5">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Video className="h-5 w-5 text-primary" />
            </div>
            {t("modals.startLiveCall.title")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-4 py-3 space-y-3">
          {/* First Name with autocomplete */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">First Name *</Label>
            <CustomerInput
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(value) => setFormData(prev => ({ ...prev, firstName: value }))}
              onCustomerSelect={(customer) => {
                // Extract phone number: always use +1 and take last 10 digits
                const digitsOnly = customer.phone_number?.replace(/\D/g, '') || "";
                const last10Digits = digitsOnly.slice(-10);

                setFormData(prev => ({
                  ...prev,
                  firstName: customer.first_name || "",
                  lastName: customer.last_name || "",
                  phoneNumber: last10Digits,
                  countryCode: "+1",
                  email: customer.email || "",
                  includeEmail: !!customer.email // Auto-enable if email exists
                }));
              }}
              className="text-xs"
              required
              field="name"
            />
          </div>

          {/* Last Name */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Last Name</Label>
            <Input
              placeholder="Enter last name (optional)"
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              className="text-xs"
            />
          </div>

          {/* Phone Number */}
          <div className="space-y-1">
            <Label htmlFor="phone" className="text-xs font-medium">
              {t("auth.phoneNumber")}
            </Label>
            <div className="flex gap-2">
              <div className="w-24">
                <Select value={formData.countryCode} onValueChange={(code) => setFormData(prev => ({ ...prev, countryCode: code }))}>
                  <SelectTrigger className="text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background border shadow-md">
                    <SelectItem value="+1">🇺🇸 +1</SelectItem>
                    <SelectItem value="+44">🇬🇧 +44</SelectItem>
                    <SelectItem value="+33">🇫🇷 +33</SelectItem>
                    <SelectItem value="+49">🇩🇪 +49</SelectItem>
                    <SelectItem value="+91">🇮🇳 +91</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <CustomerInput
                  placeholder="Phone number"
                  value={formData.phoneNumber.replace(/^\+\d+\s/, '')}
                  onChange={(value) => {
                    // Only allow digits and limit to 10
                    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
                    setFormData(prev => ({ ...prev, phoneNumber: digitsOnly }));
                  }}
                  onCustomerSelect={(customer) => {
                    // Extract phone number: always use +1 and take last 10 digits
                    const digitsOnly = customer.phone_number?.replace(/\D/g, '') || "";
                    const last10Digits = digitsOnly.slice(-10);

                    setFormData(prev => ({
                      ...prev,
                      firstName: customer.first_name || "",
                      lastName: customer.last_name || "",
                      phoneNumber: last10Digits,
                      countryCode: "+1",
                      email: customer.email || prev.email,
                      includeEmail: !!customer.email || prev.includeEmail // Auto-enable if email exists
                    }));
                  }}
                  className="text-xs"
                  required
                  field="phone_number"
                />
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">{t("header.messages")}</Label>
            <Textarea
              placeholder={t("modals.requestRecording.instructionsPlaceholder")}
              value={formData.message || getMessagePreview()}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              className="text-xs min-h-[80px]"
              autoCorrect="on"
              spellCheck="true"
            />
          </div>

          {/* Email (optional) */}
          <div className="flex items-center justify-between">
            <Label htmlFor="include-email" className="text-xs font-medium">
              Email (Optional)
            </Label>
            <Switch
              id="include-email"
              checked={formData.includeEmail}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, includeEmail: checked }))}
            />
          </div>

          {/* Email field - shown when includeEmail is true */}
          {formData.includeEmail && (
            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs font-medium">
                {t("auth.email")}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder=""
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="text-xs"
                required={formData.includeEmail}
              />
            </div>
          )}

          {/* Assign to User (Admin only) */}
          {userRole === 'admin' && (
            <div className="space-y-1">
              <Label className="text-xs font-medium">Assign to User</Label>
              <Select
                value={formData.assignTo || undefined}
                onValueChange={(value) => setFormData(prev => ({ ...prev, assignTo: value }))}
                disabled={loadingTeam || !teamMembers.length}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingTeam ? "Loading team members..." : teamMembers.length ? "Select a user" : "No users available"} />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.length ? (
                    teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="unavailable" disabled>
                      No users available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common.back")}
            </Button>
            <Button type="submit" className="px-8" disabled={isLoading || !isAuthed}>
              {isLoading ? "Sending..." : t("modals.startLiveCall.sendInvite")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
